import { useId, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { SelectInput, type SelectInputProps } from "@/shared/components/ui/SelectInput";
import { Label } from "@/shared/components/ui/Label";

/**
 * Form-field wrapper for SelectInput (design.md §10). Same mx-4 label/error
 * alignment as FormField.
 */
export interface SelectFormFieldProps extends SelectInputProps {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
}

export function SelectFormField({
  label,
  hint,
  error,
  id,
  className,
  ...selectProps
}: SelectFormFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint || error ? `${inputId}-hint` : undefined;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={inputId} className="mx-4">
        {label}
      </Label>
      <SelectInput
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={hintId}
        {...selectProps}
      />
      {error ? (
        <p id={hintId} className="mx-4 type-body-sm text-danger-strong">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mx-4 type-body-sm text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
